import { useState } from 'react';
import { Plus } from 'lucide-react';
import ImageGridItem from './ImageGridItem';

/** Grid editor for image array with drag-and-drop reordering */
export default function ImageArrayEditor({ images, onDelete, onReorder, onAdd, onDone, onImageClick, max = 6 }) {
  const [draggedIndex, setDraggedIndex] = useState(null);

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    // Reorder the array
    const newImages = [...images];
    const [removed] = newImages.splice(draggedIndex, 1);
    newImages.splice(dropIndex, 0, removed);

    onReorder?.(newImages);
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) onAdd?.(file);
  };

  // Add "add photo" slot if under max
  const showAddSlot = images.length < max;
  const slots = showAddSlot ? [...images, '__add__'] : images;

  if (images.length === 0 && !showAddSlot) {
    return (
      <div className="flex h-32 items-center justify-center rounded-xl border-2 border-dashed border-border text-muted-foreground">
        No photos yet
      </div>
    );
  }

  return (
    <div className="relative aspect-[3/4] w-full overflow-hidden bg-secondary max-h-[calc(100vh-300px)]">
      <div className="grid grid-cols-3 gap-x-2 h-full p-2 pt-14" onDragEnd={handleDragEnd}>
        {slots.map((item, index) => {
          if (item === '__add__') {
            return (
              <label
                key="add"
                className="relative aspect-[3/4] flex cursor-pointer items-center justify-center rounded-xl border-2 border-dashed border-purple-500 bg-secondary hover:border-primary/50 transition"
              >
                <div className="flex flex-col items-center gap-2">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/20 text-primary">
                    <Plus className="h-5 w-5" />
                  </div>
                  <span className="text-xs font-medium text-muted-foreground">Add photo</span>
                </div>
                <input type="file" accept="image/*" hidden onChange={handleFileChange} />
              </label>
            );
          }

          return (
            <ImageGridItem
              key={`${item}-${index}`}
              image={item}
              index={index}
              onDelete={onDelete}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              isDragging={draggedIndex === index}
              onClick={onImageClick}
            />
          );
        })}
      </div>

      {/* Done button */}
      {onDone && (
        <button
          onClick={onDone}
          className="absolute right-2 bottom-[40px] z-20 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 shadow"
          title="Done"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6 9 17l-5-5"/>
          </svg>
        </button>
      )}
    </div>
  );
}
